/**
 * KundeVorgangCard — surfaces the mandate Vorgang lifecycle on the Kunde Übersicht (#6).
 *
 * Stefan's demo showed none of the Epic-6 lifecycle on the customer page: a sent Analysemandat
 * opens a Vorgang (versandt → erinnert → überfällig → eingegangen → abgeschlossen/abgebrochen)
 * and sets the "(in Verarbeitung)" overlay, but the Übersicht never rendered any of it. This
 * card reads GET /vorgaenge/board?subjectType= (per-customer) and shows each Vorgang's
 * API-computed lifecycle: the mandate label + status (as a StatusDot) + Mahnstand + Versanddatum.
 *
 * It renders the EXISTING Vorgang statuses — it never invents a new customer enum status. The
 * "(in Verarbeitung)" overlay itself is shown on the status badge (see KundeDetailScreen); this
 * card is the lifecycle timeline the broker can point at.
 *
 * A released post or portal mandate opens no Vorgang until it is delivered; it shows here as a
 * row of its own ("freigegeben, nicht zugestellt") with the Zustellen / Freigabe zurückziehen
 * actions the caller renders (MandateDeliveryActions).
 *
 * A returned mandate whose controls opened a review (Vorgang eingegangen, the latest run an undecided
 * review) shows its Prüfbericht under the row, so the broker decides it right here.
 *
 * Returns null when there is no Vorgang and nothing pending: the common resting state.
 *
 * [Source: apps/api/src/modules/mandate/versandboard.service.ts] (displayStatus / mahnstandLabel)
 * [Source: apps/web/src/features/entity-detail/components/FirmenStammdaten.tsx] (card shell style)
 */
import { ClipboardCheck } from 'lucide-react';
import { StatusDot } from '../../../components/ui/StatusDot';
import type { StatusTone } from '../../../components/ui/StatusDot';
import { dateShortDe } from '../../../lib/format';
import type { ReactNode } from 'react';
import type { MandatePendingDeliveryWire, VorgangBoardRowWire } from '../../../lib/api-client';
import { PruefberichtContainer } from './PruefberichtContainer';

/**
 * Vorgang status → StatusDot tone. Mirrors the lifecycle severity: overdue is danger, a reminder
 * is warning, the terminal "abgeschlossen" is success, an abort is neutral, and the in-flight
 * states (versandt / eingegangen) are info.
 */
const VORGANG_TONE: Record<string, StatusTone> = {
  versandt: 'info',
  erinnert: 'warning',
  ueberfaellig: 'danger',
  eingegangen: 'info',
  abgeschlossen: 'success',
  abgebrochen: 'neutral',
};

/**
 * The mandate label for a Vorgang. docKind is the generic 'mandat'; the customer status the
 * Vorgang carries tells the two mandate kinds apart (provisorisch = Analysemandat, aktiv =
 * Brokermandat — docx Kap. 5.2). Falls back to a generic label so an unknown kind never breaks.
 */
function mandateLabel(row: VorgangBoardRowWire): string {
  if (row.customerStatus === 'provisorisch') return 'Analysemandat';
  if (row.customerStatus === 'aktiv') return 'Brokermandat';
  if (row.docKind === 'mandat') return 'Mandat';
  return row.docKind ?? 'Vorgang';
}

export function vorgangAnchorId(id: string): string {
  return `vorgang-${id}`;
}

/** A released post/portal mandate of a kind, still waiting for its delivery (no Vorgang yet). */
export interface PendingMandateDelivery {
  kind: 'analyse' | 'broker';
  delivery: MandatePendingDeliveryWire;
}

export function KundeVorgangCard({
  vorgaenge,
  pending = [],
  renderPendingActions,
}: {
  vorgaenge: VorgangBoardRowWire[];
  /** Released post/portal mandates waiting for Zustellen (they open no Vorgang until delivered). */
  pending?: PendingMandateDelivery[];
  /** The Zustellen / Freigabe zurückziehen actions for a pending row (MandateDeliveryActions). */
  renderPendingActions?: (p: PendingMandateDelivery) => ReactNode;
}) {
  if (vorgaenge.length === 0 && pending.length === 0) return null;

  return (
    <section className="flex flex-col gap-3 rounded-lg border bg-card p-4" aria-label="Vorgänge">
      <div className="flex items-center gap-2">
        <ClipboardCheck className="size-4 text-muted-foreground" aria-hidden />
        <h3 className="text-sm font-semibold text-foreground">Vorgänge</h3>
      </div>

      <ul className="flex flex-col divide-y divide-border">
        {pending.map((p) => {
          const parts = [
            `Freigegeben${p.delivery.releasedAt ? ` ${dateShortDe(p.delivery.releasedAt)}` : ''}`,
            p.delivery.channel === 'post' ? 'Post' : 'Portal',
            ...(p.delivery.processNumber ? [p.delivery.processNumber] : []),
          ];
          return (
            <li
              key={`pending-${p.delivery.mandateId}`}
              id={`mandate-pending-row-${p.delivery.mandateId}`}
              className="flex flex-wrap items-center gap-3 py-2 text-sm first:pt-0 last:pb-0"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground">
                  {p.kind === 'broker' ? 'Brokermandat' : 'Analysemandat'}
                </p>
                <p className="text-xs text-muted-foreground">{parts.join(' · ')}</p>
              </div>
              <StatusDot tone="warning" label="freigegeben, nicht zugestellt" />
              {renderPendingActions?.(p)}
            </li>
          );
        })}
        {vorgaenge.map((v) => (
          <li key={v.vorgangId} id={vorgangAnchorId(v.vorgangId)} className="flex flex-col gap-2 py-2 text-sm first:pt-0 last:pb-0">
            <div className="flex items-center gap-3">
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium text-foreground">{mandateLabel(v)}</p>
                <p className="text-xs text-muted-foreground">
                  Versandt {dateShortDe(v.sentAt)}
                  {v.mahnstandLabel && v.mahnstandLabel !== '-' ? ` · ${v.mahnstandLabel}` : ''}
                </p>
              </div>
              <StatusDot tone={VORGANG_TONE[v.status] ?? 'neutral'} label={v.displayStatus} />
            </div>
            {v.status === 'eingegangen' ? <PruefberichtContainer vorgangId={v.vorgangId} onlyInReview /> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}
